import React, { useState } from "react"
import { CloudArrowUpIcon, XMarkIcon } from "@heroicons/react/24/outline"
import LoadingOverlay from "aldoo-ra/LoadingOverlay"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Typography from "aldoo-ra/Typography"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import emailValidator from "email-validator"
import { UploadBackup, RestoreBackup } from "aldoo-ra/Backups/api"
import config from "aldoo-ra/Config"
import { setAPIAuthKey } from "aldoo-ra/API"

const InstallPage = () => {
  const [file, setFile] = useState(null)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [apiAuthKey, setApiAuthKey] = useState("")
  const [installing, setInstalling] = useState(false)
  const [activityMessage, setActivityMessage] = useState("")
  const [overlayTitle, setOverlayTitle] = useState("")
  const [uploadProgress, setUploadProgress] = useState(0)

  const handleFileSelect = () => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = ".7z"
    input.onchange = (e) => {
      const selectedFile = e.target.files[0]
      if (selectedFile) {
        setFile(selectedFile)
      }
      input.remove()
    }
    input.click()
  }

  const handleInstall = async () => {
    if (!file) {
      showMessageDialog({
        title: "Missing Archive",
        message: "Please upload a site archive file",
        variant: "error",
      })
      return
    }

    if (!emailValidator.validate(email)) {
      showMessageDialog({
        title: "Invalid Email",
        message: "Please enter a valid email address",
        variant: "error",
      })
      return
    }

    if (!apiAuthKey) {
      showMessageDialog({
        title: "Invalid API Key",
        message: "Please enter a valid API Key",
        variant: "error",
      })
      return
    }

    if (!password || password.length < 8) {
      showMessageDialog({
        title: "Invalid Password",
        message: "Password must be at least 8 characters long",
        variant: "error",
      })
      return
    }

    try {
      setInstalling(true)

      //set the API key
      setAPIAuthKey(apiAuthKey)

      // Step 1: Upload the backup file
      setOverlayTitle("Uploading site installation files...")
      const uploadResponse = await UploadBackup({
        file,
        setProgress: setUploadProgress,
      })

      if (uploadResponse.error) {
        throw new Error(uploadResponse.error)
      }

      // Step 2: Restore with specific options
      setOverlayTitle(`Installing ${config.APP_NAME}...`)
      const restoreResult = await RestoreBackup({
        run: {
          script: "restore-local",
          env: {
            BACKUP_IMAGE_FILE: file.name,
            BACKUP_IMAGE_PASSWORD: password,
            IS_REMOTE_BACKUP: false,
            DB_RESTORE_MODE: "drop",
            INCLUDE_STATIC_FILES: true,
            INCLUDE_DATABASE: true,
            INCLUDE_DEPLOYMENT: false,
            ADMIN_EMAIL: email,
          },
        },
        onResponse: (response) => {
          setActivityMessage(response.activity)
        },
      })

      if (restoreResult.error) {
        throw new Error(restoreResult.error)
      }

      showMessageDialog({
        title: "Installation Complete",
        message:
          "Your site has been successfully installed. You can now log in with your admin credentials.",
        variant: "success",
        onClose: () => {
          window.location.href = "/admin"
        },
      })
    } catch (err) {
      showMessageDialog({
        title: "Installation Failed",
        message:
          err.message ||
          "An error occurred during installation. Please try again.",
        variant: "error",
      })
    } finally {
      setInstalling(false)
      setActivityMessage("")
      setOverlayTitle("")
    }
  }

  return (
    <div className="min-h-screen bg-admin_paper p-6">
      <LoadingOverlay
        title={overlayTitle}
        message={activityMessage}
        loading={installing}
        progress={uploadProgress}
      />

      <div className="max-w-lg mx-auto mt-20">
        <div className="text-center mb-8">
          <Typography
            variant="admin_h4"
            className="text-admin_text mb-2"
          >
            Install {config.APP_NAME} Site
          </Typography>

          <Typography
            variant="admin_txt"
            className="text-admin_text"
          >
            Upload your site installation and set up credentials
          </Typography>
        </div>

        <div className="bg-admin_primary_lt/10 rounded-lg p-8 mb-6">
          <div className="flex flex-col items-center justify-center gap-4">
            {!file ? (
              <Button
                variant="admin_secondary"
                onClick={handleFileSelect}
                className="w-full flex items-center justify-center gap-2 p-4 border border-dashed border-admin_bordered rounded-lg hover:border-primary hover:bg-admin_primary"
              >
                <CloudArrowUpIcon className="w-6 h-6" />
                <span>Upload Site Installation (.7z)</span>
              </Button>
            ) : (
              <div className="w-full flex items-center justify-between p-4 bg-admin_paper rounded-lg">
                <Typography
                  variant="admin_txt"
                  className="text-admin_text truncate"
                >
                  {file.name}
                </Typography>

                <Button
                  variant="text"
                  onClick={() => setFile(null)}
                  className="text-admin_neutral_2 hover:text-admin_text_secondary"
                >
                  <XMarkIcon className="w-5 h-5" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <Typography variant="admin_txt" className="text-admin_text">
              Email
            </Typography>
            <Input
              variant="admin"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Typography variant="admin_txt" className="text-admin_text">
              Installation Serial Number
            </Typography>

            <Input
              variant="admin"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Serial number provided by Aldoo"
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Typography variant="admin_txt" className="text-admin_text">
              API Key
            </Typography>

            <Input
              variant="admin"
              type="password"
              value={apiAuthKey}
              onChange={(e) => setApiAuthKey(e.target.value)}
              placeholder={`Enter your ${config.APP_NAME} API Key`}
              className="w-full"
            />
          </div>
        </div>

        <Button
          variant="admin_primary"
          onClick={handleInstall}
          className="w-full mt-6"
        >
          Install Site
        </Button>
      </div>
    </div>
  )
}

export default InstallPage
